import React, { useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Layout } from 'antd';
import { useLocation } from 'react-router-dom';

import HorizontalDefault from '../Topbar/HorizontalDefault/index';
import HorizontalDark from '../Topbar/HorizontalDark/index';
import InsideHeader from '../Topbar/InsideHeader/index';
import AboveHeader from '../Topbar/AboveHeader/index';
import BelowHeader from '../Topbar/BelowHeader/index';

import { footerText } from 'util/config';
import App from 'routes/index';
import FromasChatAssistant from '../../components/FromasChatAssistant/index';
import SystemBanner from '../Topbar/systemBanner/index';
import {
  NAV_STYLE_ABOVE_HEADER,
  NAV_STYLE_BELOW_HEADER,
  NAV_STYLE_DARK_HORIZONTAL,
  NAV_STYLE_DEFAULT_HORIZONTAL,
  NAV_STYLE_DRAWER,
  NAV_STYLE_FIXED,
  NAV_STYLE_INSIDE_HEADER_HORIZONTAL,
  NAV_STYLE_MINI_SIDEBAR,
  NAV_STYLE_NO_HEADER_EXPANDED_SIDEBAR,
  NAV_STYLE_NO_HEADER_MINI_SIDEBAR,
  TAB_SIZE,
} from '../../constants/ThemeSetting';
import NoHeaderNotification from '../Topbar/NoHeaderNotification/index';
import { userSignOut } from 'appRedux/actions/Auth';
import { systemBannerResetSystem, systemBannerResetProperty } from 'appRedux/actions/systemBanner';
import Sidebar from '../Sidebar';
import Topbar from '../Topbar';
import { AppState } from 'ReducerState';

const { Content, Footer } = Layout;

interface MainAppProps {
  match: any;
  [key: string]: any;
}

const getContainerClass = (navStyle: string): string => {
  switch (navStyle) {
    case NAV_STYLE_DARK_HORIZONTAL:
    case NAV_STYLE_DEFAULT_HORIZONTAL:
    case NAV_STYLE_INSIDE_HEADER_HORIZONTAL:
    case NAV_STYLE_BELOW_HEADER:
    case NAV_STYLE_ABOVE_HEADER:
      return 'gx-container-wrap';
    default:
      return '';
  }
};

const getNavStyles = (navStyle: string, props: any): React.ReactNode => {
  switch (navStyle) {
    case NAV_STYLE_DEFAULT_HORIZONTAL:
      return <HorizontalDefault {...props} />;
    case NAV_STYLE_DARK_HORIZONTAL:
      return <HorizontalDark {...props} />;
    case NAV_STYLE_INSIDE_HEADER_HORIZONTAL:
      return <InsideHeader {...props} />;
    case NAV_STYLE_ABOVE_HEADER:
      return <AboveHeader {...props} />;
    case NAV_STYLE_BELOW_HEADER:
      return <BelowHeader {...props} />;
    case NAV_STYLE_FIXED:
      return <Topbar {...props} />;
    case NAV_STYLE_DRAWER:
      return <Topbar {...props} />;
    case NAV_STYLE_MINI_SIDEBAR:
      return <Topbar {...props} />;
    case NAV_STYLE_NO_HEADER_MINI_SIDEBAR:
      return <NoHeaderNotification {...props} />;
    case NAV_STYLE_NO_HEADER_EXPANDED_SIDEBAR:
      return <NoHeaderNotification {...props} />;
    default:
      return null;
  }
};

const getSidebar = (navStyle: string, width: number, props: any): React.ReactNode => {
  if (width < TAB_SIZE) {
    return <Sidebar {...props} />;
  }
  switch (navStyle) {
    case NAV_STYLE_FIXED:
    case NAV_STYLE_DRAWER:
    case NAV_STYLE_MINI_SIDEBAR:
    case NAV_STYLE_NO_HEADER_MINI_SIDEBAR:
    case NAV_STYLE_NO_HEADER_EXPANDED_SIDEBAR:
      return <Sidebar {...props} />;
    default:
      return null;
  }
};

export const MainApp = ({ match, ...rest }: MainAppProps) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const { navStyle } = useSelector((state: AppState) => state.settings);
  const { width } = useSelector((state: AppState) => state.common);
  const { authUser } = useSelector((state: AppState) => state.auth);
  const systemBanner = useSelector((state: AppState) => state.systemBanner);

  const isMountedRef = useRef(false);

  useEffect(() => {
    if (!isMountedRef.current) {
      isMountedRef.current = true;
      return;
    }
    if (authUser === null) {
      dispatch(userSignOut());
    }
  }, [authUser, dispatch]);

  // Keep --viewport-height accurate when system banners appear/disappear.
  // Tables size their scroll.y as calc(var(--viewport-height) - Npx); if banners
  // push content down without shrinking --viewport-height, pagination falls off-screen.
  useEffect(() => {
    const outer = document.querySelector<HTMLElement>('.gx-app-outer');
    const bannerEls = outer
      ? Array.from(outer.querySelectorAll<HTMLElement>('[role="status"]'))
      : [];
    const bannerHeight = bannerEls.reduce((sum, el) => sum + el.offsetHeight, 0);
    document.documentElement.style.setProperty('--banner-height', `${bannerHeight}px`);

    // Determine active zoom scale (set on document.body by ResponsiveContext)
    const notebookScale =
      parseFloat(document.body.style.getPropertyValue('--notebook-scale') || '') || 0;
    const landscapeScale =
      parseFloat(document.body.style.getPropertyValue('--landscape-scale') || '') || 0;
    const scale = notebookScale > 0 ? notebookScale : landscapeScale > 0 ? landscapeScale : 1;

    const baseH = scale < 1 ? window.innerHeight / scale : window.innerHeight;
    const effectiveH = Math.max(200, Math.round(baseH - bannerHeight / scale));
    document.documentElement.style.setProperty('--viewport-height', `${effectiveH}px`);
    document.documentElement.style.setProperty('--vh', `${effectiveH / 100}px`);
  }, [systemBanner.system, systemBanner.property]);

  const sharedProps = { match, width, navStyle, authUser, ...rest };
  const isInventoryRoute = location.pathname.startsWith('/main/inventory');

  const hideFooter = true;
  return (
    <main>
      {systemBanner.system && (
        <SystemBanner
          type={systemBanner.system.type}
          message={systemBanner.system.message}
          onClose={() => dispatch(systemBannerResetSystem())}
        />
      )}
      {systemBanner.property && (
        <SystemBanner
          type={systemBanner.property.type}
          message={systemBanner.property.message}
          onClose={() => dispatch(systemBannerResetProperty())}
        />
      )}
      <div className='w-screen h-screen overflow-hidden flex'>
        <div className='h-full'>
          <Sidebar />
        </div>
        {/* Topbar and contents */}
        <div className='flex-1 flex flex-col min-w-0 relative bg-slate-100'>
          <div>{getNavStyles(navStyle ?? '', sharedProps)}</div>
          <div
            className={`m-2 min-h-0 flex-1 overflow-x-hidden md:m-4 ${
              isInventoryRoute ? 'overflow-hidden' : 'overflow-y-auto'
            }`}
          >
            {/* content */}
            <App match={match} />
          </div>
        </div>
      </div>
    </main>
  );
};

export default MainApp;
